import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Check, Clock, LogIn, LogOut, Pencil, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import VenturaLogo from "@/components/VenturaLogo";

export default function ValidarJornadaWhatsApp() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [modo, setModo] = useState("resumen"); // resumen | modificar | ok
  const [hora, setHora] = useState("");
  const [confirmacion, setConfirmacion] = useState("");
  const autoHecho = useRef(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError("");
    try {
      const { data: d } = await api.get(`/fichaje-whatsapp/${token}`);
      setData(d);
      setHora(d.hora_sugerida || d.entrada_propuesta || "");
      if (d.mensaje_confirmacion && (d.ya_tiene_fichajes || d.estado === "parcial")) {
        setConfirmacion(d.mensaje_confirmacion);
      }
      if (d.ya_tiene_fichajes) {
        setModo("ok");
      } else if (d.puede_actuar && searchParams.get("accion") === "modificar") {
        setModo("modificar");
      } else {
        setModo("resumen");
      }
    } catch (e) {
      setError(formatApiError(e));
      setData(null);
    } finally {
      setCargando(false);
    }
  }, [token, searchParams]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    if (!data?.puede_actuar || enviando || autoHecho.current) return;
    if (searchParams.get("accion") !== "registrar") return;
    autoHecho.current = true;
    (async () => {
      setEnviando(true);
      setError("");
      try {
        const { data: d } = await api.post(`/fichaje-whatsapp/${token}/registrar`);
        setData(d);
        setConfirmacion(d.mensaje_confirmacion || "Registro realizado correctamente.");
        setModo("ok");
      } catch (e) {
        setError(formatApiError(e));
        setModo("resumen");
      } finally {
        setEnviando(false);
      }
    })();
  }, [data?.puede_actuar, searchParams, token, enviando, autoHecho]);

  const registrar = async () => {
    setEnviando(true);
    setError("");
    try {
      const { data: d } = await api.post(`/fichaje-whatsapp/${token}/registrar`);
      setData(d);
      setConfirmacion(d.mensaje_confirmacion || "Registro realizado correctamente.");
      setModo("ok");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setEnviando(false);
    }
  };

  const modificar = async () => {
    setEnviando(true);
    setError("");
    try {
      const { data: d } = await api.post(`/fichaje-whatsapp/${token}/modificar`, { hora });
      setData(d);
      setConfirmacion(d.mensaje_confirmacion || "Picado manual registrado.");
      setModo("ok");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setEnviando(false);
    }
  };

  if (cargando) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-violet-50 to-white">
        <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-violet-50 to-white px-4">
        <div className="max-w-md rounded-2xl border border-violet-100 bg-white p-8 text-center shadow-sm">
          <VenturaLogo className="mx-auto h-10 w-auto" />
          <p className="mt-6 text-lg font-medium text-zinc-900">Enlace no válido</p>
          <p className="mt-2 text-sm text-zinc-500">{error || "No encontramos esta invitación."}</p>
        </div>
      </div>
    );
  }

  const SiguienteIcon = data.siguiente === "salida" ? LogOut : LogIn;
  const labelAccion = data.siguiente_label || "picado";

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 via-fuchsia-50/40 to-white px-4 py-8">
      <div className="mx-auto max-w-md">
        <div className="mb-6 text-center">
          {data.logo_url ? (
            <img
              src={data.logo_url}
              alt={data.empresa || "Logo empresa"}
              className="mx-auto h-14 w-auto max-w-[220px] object-contain"
            />
          ) : (
            <VenturaLogo className="mx-auto h-12 w-auto" />
          )}
          <p className="mt-3 text-xs font-medium uppercase tracking-[0.14em] text-violet-700/80">
            Registro de jornada
          </p>
          <h1 className="mt-1 font-display text-2xl font-semibold tracking-tight text-zinc-950">
            {modo === "ok" ? "Confirmación" : "Tu jornada"}
          </h1>
          <p className="mt-1 text-sm font-medium text-zinc-800">{data.nombre}</p>
          {(data.empresa || data.centro) && (
            <p className="mt-0.5 text-sm text-zinc-500">
              {[data.empresa, data.centro].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>

        <div className="rounded-2xl border border-violet-100 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-violet-800">
              <Clock className="h-4 w-4" />
              <span className="text-sm font-medium">{data.fecha_txt}</span>
            </div>
            <span className="rounded-full bg-violet-50 px-2.5 py-0.5 text-[11px] font-medium text-violet-700">
              {data.entrada_propuesta} – {data.salida_propuesta}
            </span>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <div
              className={`rounded-xl border px-3 py-2 text-center ${
                data.tiene_entrada || data.entrada_registrada
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : data.siguiente === "entrada"
                    ? "border-violet-300 bg-violet-50 text-violet-900 ring-1 ring-violet-200"
                    : "border-zinc-100 bg-zinc-50 text-zinc-400"
              }`}
            >
              <LogIn className="mx-auto h-4 w-4" />
              <p className="mt-1 text-[11px] font-medium">Entrada</p>
              <p className="text-sm tabular-nums font-semibold">
                {data.entrada_registrada || (data.siguiente === "entrada" ? data.hora_sugerida : data.entrada_propuesta)}
              </p>
              {(data.tiene_entrada || data.entrada_registrada) && (
                <p className="text-[10px] text-emerald-700">Hecha</p>
              )}
            </div>
            <div
              className={`rounded-xl border px-3 py-2 text-center ${
                data.tiene_salida || data.salida_registrada
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : data.siguiente === "salida"
                    ? "border-violet-300 bg-violet-50 text-violet-900 ring-1 ring-violet-200"
                    : "border-zinc-100 bg-zinc-50 text-zinc-400"
              }`}
            >
              <LogOut className="mx-auto h-4 w-4" />
              <p className="mt-1 text-[11px] font-medium">Salida</p>
              <p className="text-sm tabular-nums font-semibold">
                {data.salida_registrada || (data.siguiente === "salida" ? data.hora_sugerida : data.salida_propuesta)}
              </p>
              {(data.tiene_salida || data.salida_registrada) && (
                <p className="text-[10px] text-emerald-700">Hecha</p>
              )}
              {!data.tiene_entrada && !data.entrada_registrada && data.siguiente === "entrada" && (
                <p className="text-[10px] text-zinc-400">Después</p>
              )}
            </div>
          </div>

          {modo === "ok" ? (
            <div className="mt-6 space-y-3 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-violet-100 text-violet-700">
                <Check className="h-6 w-6" />
              </div>
              <p className="font-medium text-zinc-900">Registro confirmado</p>
              <p className="text-sm text-zinc-600">
                {confirmacion || data.mensaje_confirmacion || "Operación realizada correctamente."}
              </p>
              {(data.entrada_registrada || data.salida_registrada) && (
                <div className="mt-3 rounded-xl bg-violet-50 px-4 py-3 text-sm text-violet-950">
                  {data.entrada_registrada && (
                    <p>
                      Entrada: <strong className="tabular-nums">{data.entrada_registrada}</strong>
                    </p>
                  )}
                  {data.salida_registrada && (
                    <p>
                      Salida: <strong className="tabular-nums">{data.salida_registrada}</strong>
                    </p>
                  )}
                </div>
              )}
              {data.puede_actuar && data.siguiente && (
                <Button
                  className="mt-2 w-full bg-violet-600 hover:bg-violet-700"
                  onClick={() => {
                    setConfirmacion("");
                    setHora(data.hora_sugerida || "");
                    setModo("resumen");
                  }}
                >
                  Continuar con {data.siguiente_label}
                </Button>
              )}
            </div>
          ) : !data.puede_actuar ? (
            <div className="mt-6 space-y-2 text-sm text-zinc-600">
              {data.ya_tiene_fichajes && (
                <p>Ya tienes entrada y salida registradas para este día.</p>
              )}
              {data.caducado && <p>Este enlace ha caducado.</p>}
              {!data.ya_tiene_fichajes && !data.caducado && (
                <p>No hay picados pendientes en este enlace.</p>
              )}
            </div>
          ) : modo === "modificar" ? (
            <div className="mt-6 space-y-4">
              <div className="rounded-xl border border-violet-100 bg-violet-50/60 px-4 py-3">
                <p className="text-xs text-violet-700/80">Picado manual</p>
                <p className="mt-0.5 text-sm font-medium text-violet-950">
                  Selecciona la hora de <strong>{labelAccion.toLowerCase()}</strong>
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Hora de {labelAccion.toLowerCase()}</Label>
                <Input type="time" value={hora} onChange={(e) => setHora(e.target.value)} />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 border-violet-200"
                  onClick={() => setModo("resumen")}
                  disabled={enviando}
                >
                  Atrás
                </Button>
                <Button
                  className="flex-1 bg-violet-600 hover:bg-violet-700"
                  onClick={modificar}
                  disabled={enviando || !hora}
                >
                  {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar picado"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-6 space-y-5">
              <div className="rounded-xl bg-gradient-to-br from-violet-50 to-fuchsia-50 px-4 py-4 text-center border border-violet-100">
                <p className="text-xs text-violet-700/80">Siguiente picado</p>
                <div className="mt-2 flex items-center justify-center gap-2 text-violet-950">
                  <SiguienteIcon className="h-5 w-5" />
                  <span className="text-lg font-semibold">{labelAccion}</span>
                </div>
                {data.modo_picado === "manual" ? (
                  <>
                    <p className="mt-1 text-2xl font-semibold tabular-nums text-violet-950">
                      {data.hora_sugerida}
                    </p>
                    <p className="mt-1 text-xs text-zinc-500">Hora prevista según tu horario</p>
                  </>
                ) : (
                  <>
                    <p className="mt-1 text-2xl font-semibold tabular-nums text-violet-950">
                      {data.hora_registro || "ahora"}
                    </p>
                    <p className="mt-1 text-xs text-zinc-500">
                      Se registrará la hora exacta al pulsar (horario previsto {data.hora_sugerida})
                    </p>
                  </>
                )}
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex flex-col gap-2">
                <Button
                  className="w-full bg-violet-600 hover:bg-violet-700"
                  onClick={registrar}
                  disabled={enviando}
                >
                  {enviando ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <SiguienteIcon className="mr-2 h-4 w-4" />
                      Registrar {labelAccion.toLowerCase()}
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-violet-200 text-violet-900"
                  onClick={() => setModo("modificar")}
                  disabled={enviando}
                >
                  <Pencil className="mr-2 h-4 w-4" /> Modificar hora
                </Button>
              </div>
              <p className="text-center text-[11px] text-zinc-400">
                {data.modo_picado === "manual"
                  ? "«Registrar» usa la hora prevista. «Modificar hora» te permite elegir la hora a mano."
                  : "«Registrar» usa la hora real ahora. «Modificar hora» permite elegir otra hora (picado manual)."}
              </p>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-zinc-400">
          Ventura HR · Si no trabajaste, no registres y habla con tu responsable.
        </p>
      </div>
    </div>
  );
}
